/*
 * Copyright (c) 2023 Pegasystems Inc.
 * All rights reserved.
 */
import {
  getChanges,
  isContextDirty,
  resetChangedProperties
} from '../store/state/middlewares/property-change-middleware';
import { isFormValid, getEditableFields } from '../utils/form-handler';
import { clearInstructions, clearPageInstructionReferences } from '../page-instructions';
import validator from '../validation/validator';
import { getSubmitData } from '../store/state/epics/util';
/**
 * @description
 * The FormUtilsclass contains utility APIs that handle form related scenarios
 */
class FormUtils {
  /**
   * This API returns the changed data of a given container's context
   * @param context - The name of the context whose changed data information must be obtained
   * @returns the changes state information
   * @public
   * @example <caption> getChanges API will return all the information about the data that has changed for that context</caption>
   * PCore.getFormUtils().getChanges("app/primary_1/workarea_1");
   * //output
   * {
   *  caseInfo: {
   *    content: {
   *        "name": "Optimus",
   *    }
   *   }
   * }
   */

  static getChanges(context: string): object {
    const changedProperties = getChanges(context);
    return {
      ...changedProperties
    };
  }

  /**
   * This API returns the submittable data of a given context
   * @param context - The name of the context whose submittable data needs to return
   * @param options - The Javascript object that contains optional properties that provide additional information for getting the submittable data
   * @param options.isTransientContext - Flag indicating whether the context is transient when retrieving submittable data.
   * - `true` : get submittable data from transient context.
   * - `false` : default value, get submittable data from non transient context.
   * @param {boolean} [options.includeDisabledFields] Flag indicating whether to include disabled field.
   * - `true`: include disabled fields inside of context.
   * - `false`: default value, dont include disabled fields.
   * @returns submittable data
   * @example getSubmitData()
   * getSubmitData API will return the submittable data from the given context
   * ```
   * PCore.getFormUtils().getSubmitData("app/primary_1/workarea_1",{isTransientContext : false});
   * ```
   * return
   * {
   *  caseInfo: {
   *    content: {
   *        "name": "Optimus",
   *    }
   *   }
   * }
   */
  static getSubmitData(context: string, options?: { isTransientContext?: boolean; includeDisabledFields?: boolean }) {
    const { isTransientContext = false, includeDisabledFields = false } = options || {};
    const { content } = getSubmitData(context, { isTransientContext, includeDisabledFields });
    return content;
  }

  /**
   * This API returns whether a Redux State is modified or not for the given context.
   * @param context - The name of the context.
   * @returns returns true if redux state is modified for the given context else return false.
   * @public
   * @example <caption> isStateModified API will return true if the redux state that has changed for the given context otherwise it return false.</caption>
   * PCore.getFormUtils().isStateModified("app/primary_1/workarea_1");
   * //output
   * true
   */
  static isStateModified(context: string | null | undefined) {
    return isContextDirty(context);
  }

  /**
   * This API verifies whether form is valid or not based on the validation values for form fields
   * @param context context of form fields to be validated
   * @param pageReference Embed page reference property path e.g. .pyWorkPage, D_Accounts.pxResults(1)
   * @returns
   * - `true` : form is valid based on the validation values for form fields
   * - `false` : form is invalid based on the validation values for form fields
   * @public
   * @example isFormValid()
   * In this example check whether form is valid or not in "app/primary_1/workarea_1" context inside "D_Accounts.pxResults[1]"
   * page reference
   * ```
   * const isFormValid = PCore.getFormUtils().isFormValid("app/primary_1/workarea_1", "D_Accounts.pxResults[1]");
   * ```
   */

  static isFormValid(context: string, pageReference?: string) {
    return isFormValid(context, pageReference);
  }

  /**
   * This API clears the changed properties object of the context.
   * @param context The name of the context whose changed data information must be cleared
   * @public
   * @example <caption>In this example clears the changed properties</caption>
   * PCore.getFormUtils().clearChangedProperties("app/primary_1/workarea_1");
   * Note : This API clears the changed properties object. Changed properties object  is used to know the form dirty status and during form submission, but
   * it will not clear the Redux state and form fields. After this API call if we try to get the changes using getChanges API we will get empty object.
   */

  static clearChangedProperties(context: string) {
    clearInstructions(context);
    clearPageInstructionReferences(context);
    return resetChangedProperties(context);
  }

  /**
   * This API is to get the list of editable fields information from a context
   * @param context context of editable form fields
   * @returns editable fields information in an array
   * @public
   * @example <caption>In this example bring all the editable fields from given context</caption>
   * const editableFields = PCore.getFormUtils().getEditableFields("app/primary_1/workarea_1");
   * [{
    "name": "caseInfo.content.City",
    "label": "city",
    "type": "textinput"
    }]
   */

  static getEditableFields(context: string): any[] {
    return getEditableFields(context);
  }

  /**
   * This API registers the custom validator function to the validators object which holds all the validator functions.
   * @param type type of the validation like email etc.,
   * @param validatorFn validator function which contains the code logic to perform validation.
   *
   * @example <caption>Example for PCore.getFormUtils().setCustomValidator(type, fn)</caption>
   * PCore.getFormUtils().setCustomValidator('isAlpha', () => { // implementation code goes here..  // })
   * 'This example will register the isAlpha validator function in validators object with key as "isAlpha" and value as second parameter passed to this function'
   */
  static setCustomValidator(type: string, validatorFn: Function) {
    validator.register(type, validatorFn);
  }

  /**
   * This API is to check whether the provided property is editable or not for the given context.
   * @param context context of editable form fields eg.. app/primary_1/workarea_1
   * @param propertyReference complete reference path of the property eg.. caseInfo.content.PageList[0].Name
   * @returns returns true if the provided property is Editable else returns false
   * @example <caption>Example for PCore.getFormUtils().isPropertyEditable('app/primary_1/workarea_1', 'caseInfo.content.PageList[0].Name')</caption>
   * The above example depicts whether the property 'Name' with reference 'caseInfo.content.PageList[0].Name' is editable or not.
   * @private
   */
  static isPropertyEditable(context: string, propertyReference: string) {
    const editableFields = FormUtils.getEditableFields(context);
    /* break the loop as soon as field is found */
    return editableFields.some((fieldObject) => fieldObject.name === propertyReference);
  }
}

export default FormUtils;
